import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { LLMProvider, ProviderType, LLMModel } from '../../types/admin';
import { Plus, XCircle } from 'lucide-react';
import clsx from 'clsx';

interface LLMProviderDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (provider: Partial<LLMProvider>) => Promise<void>;
  provider?: LLMProvider;
  mode: 'create' | 'edit';
}

interface ModelFormData {
  id: string;
  name: string;
  display_name: string;
  max_tokens: number;
  temperature: number;
  default_for: string[];
  enabled: boolean;
}

interface FormData {
  name: string;
  provider: ProviderType;
  endpoint: string;
  api_key: string;
  rate_limits: {
    requests_per_minute: number;
    tokens_per_minute: number;
    concurrent_requests: number;
  };
  models: ModelFormData[];
}

export const LLMProviderDialog: React.FC<LLMProviderDialogProps> = ({
  open,
  onClose,
  onSave,
  provider,
  mode,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    provider: 'openai',
    endpoint: '',
    api_key: '',
    rate_limits: {
      requests_per_minute: 10000,
      tokens_per_minute: 150000,
      concurrent_requests: 5,
    },
    models: [],
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [loading, setLoading] = useState(false);

  const resetForm = useCallback(() => {
    setFormData({
      name: provider?.name || '',
      provider: provider?.provider || 'openai',
      endpoint: provider?.endpoint || '',
      api_key: '',
      rate_limits: provider?.rate_limits || {
        requests_per_minute: 10000,
        tokens_per_minute: 150000,
        concurrent_requests: 5,
      },
      models:
        provider?.models.map(m => ({
          id: m.id,
          name: m.name,
          display_name: m.display_name,
          max_tokens: m.max_tokens,
          temperature: m.temperature,
          default_for: m.default_for || [],
          enabled: m.enabled,
        })) || [],
    });
    setErrors({});
    setLoading(false);
  }, [provider]);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Provider name is required';
    }

    if (!formData.endpoint.trim()) {
      newErrors.endpoint = 'API endpoint is required';
    } else if (!/^https?:\/\/.+/.test(formData.endpoint)) {
      newErrors.endpoint = 'Please enter a valid URL (http:// or https://)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const providerData: Partial<LLMProvider> = {
        ...formData,
        models: formData.models.map(
          m =>
            ({
              id: m.id,
              name: m.name,
              display_name: m.display_name,
              provider_id: '', // Will be set by backend
              max_tokens: m.max_tokens,
              temperature: m.temperature,
              default_for: m.default_for,
              enabled: m.enabled,
            }) as LLMModel
        ),
      };
      await onSave(providerData);
      onClose();
    } catch (_error) {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleRateLimitChange = (field: keyof FormData['rate_limits'], value: number) => {
    setFormData(prev => ({
      ...prev,
      rate_limits: { ...prev.rate_limits, [field]: value },
    }));
  };

  const addModel = () => {
    const newModel: ModelFormData = {
      id: `model-${Date.now()}`,
      name: '',
      display_name: '',
      max_tokens: 4096,
      temperature: 0.7,
      default_for: [],
      enabled: true,
    };
    setFormData(prev => ({ ...prev, models: [...prev.models, newModel] }));
  };

  const updateModel = (index: number, field: keyof ModelFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      models: prev.models.map((m, i) => (i === index ? { ...m, [field]: value } : m)),
    }));
  };

  const removeModel = (index: number) => {
    setFormData(prev => ({
      ...prev,
      models: prev.models.filter((_, i) => i !== index),
    }));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        mode === 'create' ? t('llmConfig.dialog.addProvider') : t('llmConfig.dialog.editProvider')
      }
      maxWidth='xl'
      showCloseButton={!loading}
      footer={
        <div className='flex justify-end space-x-3'>
          <button
            type='button'
            onClick={onClose}
            disabled={loading}
            className='px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {t('common.cancel')}
          </button>
          <button
            type='button'
            onClick={handleSubmit}
            disabled={loading}
            className={clsx(
              'px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
              'bg-blue-600 text-white hover:bg-blue-700'
            )}
          >
            {loading ? t('llmConfig.dialog.saving') : t('common.save')}
          </button>
        </div>
      }
    >
      <form onSubmit={e => e.preventDefault()} className='space-y-6'>
        {/* Provider Name */}
        <div>
          <label htmlFor='name' className='block text-sm font-medium text-gray-700 mb-1'>
            {t('llmConfig.dialog.nameLabel')} *
          </label>
          <input
            id='name'
            type='text'
            value={formData.name}
            onChange={e => handleChange('name', e.target.value)}
            disabled={loading}
            placeholder='My OpenAI Instance'
            className={clsx(
              'w-full px-3 py-2 border rounded-lg',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              'disabled:bg-gray-100 disabled:cursor-not-allowed',
              errors.name ? 'border-red-500' : 'border-gray-300'
            )}
          />
          {errors.name && <p className='mt-1 text-sm text-red-600'>{errors.name}</p>}
        </div>

        {/* Provider Type */}
        <div>
          <label htmlFor='provider' className='block text-sm font-medium text-gray-700 mb-1'>
            {t('llmConfig.dialog.typeLabel')}
          </label>
          <select
            id='provider'
            value={formData.provider}
            onChange={e => handleChange('provider', e.target.value as ProviderType)}
            disabled={loading || mode === 'edit'}
            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed'
          >
            <option value='openai'>OpenAI</option>
            <option value='anthropic'>Anthropic</option>
            <option value='ollama'>Ollama</option>
            <option value='custom'>Custom</option>
          </select>
        </div>

        {/* API Endpoint */}
        <div>
          <label htmlFor='endpoint' className='block text-sm font-medium text-gray-700 mb-1'>
            {t('llmConfig.apiEndpoint')} *
          </label>
          <input
            id='endpoint'
            type='url'
            value={formData.endpoint}
            onChange={e => handleChange('endpoint', e.target.value)}
            disabled={loading}
            placeholder='https://api.openai.com/v1'
            className={clsx(
              'w-full px-3 py-2 border rounded-lg',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              'disabled:bg-gray-100 disabled:cursor-not-allowed',
              errors.endpoint ? 'border-red-500' : 'border-gray-300'
            )}
          />
          {errors.endpoint && <p className='mt-1 text-sm text-red-600'>{errors.endpoint}</p>}
        </div>

        {/* API Key */}
        <div>
          <label htmlFor='api_key' className='block text-sm font-medium text-gray-700 mb-1'>
            {t('llmConfig.apiKey')}
          </label>
          <input
            id='api_key'
            type='password'
            value={formData.api_key}
            onChange={e => handleChange('api_key', e.target.value)}
            disabled={loading}
            placeholder='sk-...'
            autoComplete='off'
            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed font-mono text-sm'
          />
          <p className='mt-1 text-xs text-gray-500'>
            {mode === 'create'
              ? t('llmConfig.dialog.apiKeyHelp')
              : t('llmConfig.dialog.apiKeyEditHelp')}
          </p>
        </div>

        {/* Rate Limits */}
        <div className='grid grid-cols-3 gap-4'>
          <div>
            <label htmlFor='rpm' className='block text-sm font-medium text-gray-700 mb-1'>
              {t('llmConfig.dialog.rpmLabel')}
            </label>
            <input
              id='rpm'
              type='number'
              value={formData.rate_limits.requests_per_minute}
              onChange={e =>
                handleRateLimitChange('requests_per_minute', parseInt(e.target.value) || 0)
              }
              disabled={loading}
              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed'
            />
          </div>
          <div>
            <label htmlFor='tpm' className='block text-sm font-medium text-gray-700 mb-1'>
              {t('llmConfig.dialog.tpmLabel')}
            </label>
            <input
              id='tpm'
              type='number'
              value={formData.rate_limits.tokens_per_minute}
              onChange={e =>
                handleRateLimitChange('tokens_per_minute', parseInt(e.target.value) || 0)
              }
              disabled={loading}
              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed'
            />
          </div>
          <div>
            <label htmlFor='concurrent' className='block text-sm font-medium text-gray-700 mb-1'>
              {t('llmConfig.dialog.concurrentLabel')}
            </label>
            <input
              id='concurrent'
              type='number'
              value={formData.rate_limits.concurrent_requests}
              onChange={e =>
                handleRateLimitChange('concurrent_requests', parseInt(e.target.value) || 0)
              }
              disabled={loading}
              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed'
            />
          </div>
        </div>

        {/* Models */}
        <div>
          <div className='flex items-center justify-between mb-2'>
            <label className='block text-sm font-medium text-gray-700'>
              {t('llmConfig.models')}
            </label>
            <button
              type='button'
              onClick={addModel}
              disabled={loading}
              className='flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              <Plus size={16} />
              <span>{t('llmConfig.dialog.addModel')}</span>
            </button>
          </div>

          {formData.models.length === 0 ? (
            <p className='text-sm text-gray-500 italic'>{t('llmConfig.dialog.noModels')}</p>
          ) : (
            <div className='space-y-4 max-h-60 overflow-y-auto'>
              {formData.models.map((model, index) => (
                <div key={model.id} className='border border-gray-200 rounded-lg p-3 space-y-3'>
                  <div className='flex items-start justify-between'>
                    <div className='flex-1 grid grid-cols-2 gap-3'>
                      <div>
                        <input
                          type='text'
                          value={model.name}
                          onChange={e => updateModel(index, 'name', e.target.value)}
                          disabled={loading}
                          placeholder='gpt-4'
                          className='w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100'
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                          {t('llmConfig.dialog.modelName')}
                        </p>
                      </div>
                      <div>
                        <input
                          type='text'
                          value={model.display_name}
                          onChange={e => updateModel(index, 'display_name', e.target.value)}
                          disabled={loading}
                          placeholder='GPT-4'
                          className='w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100'
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                          {t('llmConfig.dialog.displayName')}
                        </p>
                      </div>
                    </div>
                    <button
                      type='button'
                      onClick={() => removeModel(index)}
                      disabled={loading}
                      className='ml-2 p-1 text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      <XCircle size={18} />
                    </button>
                  </div>

                  <div className='grid grid-cols-3 gap-3'>
                    <div>
                      <input
                        type='number'
                        value={model.max_tokens}
                        onChange={e =>
                          updateModel(index, 'max_tokens', parseInt(e.target.value) || 0)
                        }
                        disabled={loading}
                        className='w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100'
                      />
                      <p className='text-xs text-gray-500 mt-1'>
                        {t('llmConfig.dialog.maxTokens')}
                      </p>
                    </div>
                    <div>
                      <input
                        type='number'
                        step='0.1'
                        min='0'
                        max='2'
                        value={model.temperature}
                        onChange={e =>
                          updateModel(index, 'temperature', parseFloat(e.target.value))
                        }
                        disabled={loading}
                        className='w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100'
                      />
                      <p className='text-xs text-gray-500 mt-1'>
                        {t('llmConfig.dialog.temperature')}
                      </p>
                    </div>
                    <div className='flex items-center'>
                      <label className='flex items-center space-x-2 text-sm'>
                        <input
                          type='checkbox'
                          checked={model.enabled}
                          onChange={e => updateModel(index, 'enabled', e.target.checked)}
                          disabled={loading}
                          className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                        />
                        <span>{t('llmConfig.dialog.modelEnabled')}</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default LLMProviderDialog;
